import { AlertTriangle, ExternalLink, Loader2, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { isApiError, type OutOfSyncProject, useDeleteAdminProject } from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";

/** Whether a refused delete was refused for the project's live app registrations. */
function holdsRegistrations(err: unknown): boolean {
  if (!isApiError(err)) {
    return false;
  }
  const detail = (err.body as { detail?: unknown } | undefined)?.detail;
  return (
    detail !== null &&
    typeof detail === "object" &&
    Array.isArray((detail as { app_registrations?: unknown }).app_registrations)
  );
}

interface OutOfSyncDialogProps {
  projects: OutOfSyncProject[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolved: () => void;
}

export function OutOfSyncDialog({
  projects,
  open,
  onOpenChange,
  onResolved,
}: OutOfSyncDialogProps) {
  const deleteProject = useDeleteAdminProject();
  const [remaining, setRemaining] = useState<OutOfSyncProject[]>(projects);
  const [deleting, setDeleting] = useState<Set<number>>(new Set());
  // Why a delete was refused, per project -- a project still holding live app
  // registrations is refused (409) until an admin decommissions them or deletes
  // it with a recorded reason from Admin > Projects.
  const [errors, setErrors] = useState<Record<number, string>>({});
  const prevRemainingLen = useRef(remaining.length);

  // Keep remaining in sync when the dialog opens with new projects,
  // and reset the previous-length ref so transition tracking starts fresh.
  useEffect(() => {
    if (!open) {
      prevRemainingLen.current = 0;
      return;
    }
    if (projects.length > 0) {
      setRemaining(projects);
      prevRemainingLen.current = projects.length;
    }
  }, [open, projects]);

  // Auto-resolve only on an actual transition from non-empty -> empty remaining
  useEffect(() => {
    if (!open) {
      return;
    }
    const becameEmpty = prevRemainingLen.current > 0 && remaining.length === 0;
    prevRemainingLen.current = remaining.length;
    if (becameEmpty) {
      onResolved();
    }
  }, [open, remaining.length, onResolved]);

  const handleDelete = async (project: OutOfSyncProject) => {
    setDeleting((prev) => new Set([...prev, project.id]));
    setErrors(({ [project.id]: _cleared, ...rest }) => rest);
    try {
      await deleteProject.mutateAsync({ projectId: project.id });
      setRemaining((prev) => prev.filter((p) => p.id !== project.id));
    } catch (err) {
      logger.error("[out-of-sync] delete failed:", err);
      const message = readApiErrorMessage(err, "Could not delete this project.");
      setErrors((prev) => ({
        ...prev,
        [project.id]: holdsRegistrations(err)
          ? `${message} To delete it with them, use Admin > Projects and give a reason.`
          : message,
      }));
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(project.id);
        return next;
      });
    }
  };

  const handleClose = (v: boolean) => {
    if (!v) {
      // Only treat as "resolved" if all items were actually deleted;
      // otherwise just dismiss without triggering a full refresh.
      if (remaining.length === 0 && projects.length > 0) {
        onResolved();
      }
      prevRemainingLen.current = 0;
      setRemaining([]);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-amber-500" />
            Out-of-sync projects found
          </DialogTitle>
          <DialogDescription>
            The following projects exist in the database but their GitHub repositories could not be
            found. Verify each repo and remove the ones that are truly gone.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {remaining.map((project) => (
              <div
                key={project.id}
                className="flex items-center justify-between gap-3 rounded-lg border p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-foreground truncate text-sm font-medium">
                    {project.project_name}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">{project.project_slug}</p>
                  {errors[project.id] && (
                    <p className="text-destructive mt-1 text-xs">{errors[project.id]}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {project.github_url && (
                    <a href={project.github_url} target="_blank" rel="noopener noreferrer">
                      <Button size="icon" variant="ghost" className="size-8">
                        <ExternalLink className="size-3.5" />
                      </Button>
                    </a>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-8 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30"
                    onClick={() => handleDelete(project)}
                    disabled={deleting.has(project.id)}
                  >
                    {deleting.has(project.id) ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="size-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => handleClose(false)}>
              Done
            </Button>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
